// ===========================================
// Late fees on one invoice (admin) — #372 plan A6.3
// ===========================================
// Every fee row, in order. A waived fee stays, struck through, with who waived it and why (staff
// only). Waive needs `operations_invoices.write` AND `operations_pricing.delete` (the server's
// pair); without it the button is not shown.

import { useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { Invoice, InvoiceLateFee, LateFeePreview } from '../../api/invoices.api';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { formatInSchoolTz, formatUTCDate } from '../../utils/dateUtils';
import { useWaiveLateFee } from '../../hooks/useInvoices';
import { useLateFeePreview } from '../../hooks/useLateFeePreview';
import { dayKeyToDisplay } from '../../utils/describeLateFeeChange';
import LateFeeReasonDialog from './LateFeeReasonDialog';

const DAY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export interface InvoiceLateFeesSectionProps {
  invoice: Pick<Invoice, 'id' | 'status' | 'InvoiceLateFee'>;
  timeZone: string;
  /** operations_invoices.write AND operations_pricing.delete */
  canWaive: boolean;
}

function FeeRow({
  fee,
  timeZone,
  onWaive,
}: {
  fee: InvoiceLateFee;
  timeZone: string;
  onWaive?: () => void;
}) {
  const amount = formatCurrencyFromDollars(parseFloat(fee.amount));
  const charged = formatInSchoolTz(fee.appliedAt, timeZone, DAY);
  const scheduled = formatUTCDate(fee.scheduledFor, { day: 'numeric', month: 'short' });
  const waived = fee.waivedAt !== null;
  return (
    <Box sx={{ p: 1.5, bgcolor: waived ? 'grey.100' : '#FCF6E6', borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Late fee {fee.sequence} ·{' '}
            <Box component="span" sx={waived ? { textDecoration: 'line-through' } : undefined}>
              {amount}
            </Box>
            {fee.cappedBy === 'TOTAL' && ' (what was left of the cap)'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Charged {charged} (scheduled {scheduled}) ·{' '}
            {fee.notifiedAt ? 'family emailed' : 'family not emailed'}
          </Typography>
          {waived && (
            <Typography variant="body2" color="text.secondary">
              Waived {fee.waivedAt ? formatInSchoolTz(fee.waivedAt, timeZone, DAY) : ''}
              {fee.waivedByName ? ` by ${fee.waivedByName}` : ''}
              {fee.waiverReason ? `: ${fee.waiverReason}` : ''}
            </Typography>
          )}
        </Box>
        {waived ? (
          <Chip label="Waived" size="small" />
        ) : (
          onWaive && (
            <Button size="small" variant="outlined" onClick={onWaive}>
              Waive
            </Button>
          )
        )}
      </Stack>
    </Box>
  );
}

const BLOCK_REASON: Record<Extract<LateFeePreview, { state: 'BLOCKED' }>['reason'], string> = {
  FAMILY_EXEMPT: 'this family is exempt from late fees',
  ON_HOLD: "this invoice's reminders are on hold",
  KIND_NOT_CHARGED: 'this kind of invoice is not charged late fees',
  REPEATS_OFF: 'repeat fees are switched off',
  AT_MAX_COUNT: 'the most fees on one invoice has been reached',
  AT_CAP: 'the most in fees on one invoice has been reached',
  BELOW_MIN_OWING: 'the balance is below the minimum that is charged a fee',
  ROUNDS_TO_ZERO: 'the fee would round to nothing',
  CHECKOUT_PENDING: 'the family is paying online',
};

/** The preview as one sentence, or null when there is nothing to say. Exported for tests. */
export function describeLateFeePreview(p: LateFeePreview): string | null {
  switch (p.state) {
    case 'NEXT':
      return `Next late fee: ${formatCurrencyFromDollars(parseFloat(p.amount))} ${
        p.dueToday ? 'today' : `on ${dayKeyToDisplay(p.date)}`
      }`;
    case 'WAITING_LAST_CHANCE':
      return 'No late fee until the Last Chance email has reached the family.';
    case 'BLOCKED':
      return `No late fee on ${dayKeyToDisplay(p.date)}: ${BLOCK_REASON[p.reason] ?? 'it will not be charged'}.`;
    default:
      return null;
  }
}

// A paid, void or draft invoice has no next fee; do not ask.
const NO_PREVIEW_STATUS = new Set(['DRAFT', 'PAID', 'CANCELLED', 'REFUNDED']);

export default function InvoiceLateFeesSection({ invoice, timeZone, canWaive }: InvoiceLateFeesSectionProps) {
  const fees = invoice.InvoiceLateFee ?? [];
  const waiveMutation = useWaiveLateFee();
  const previewEnabled = !NO_PREVIEW_STATUS.has(invoice.status);
  const preview = useLateFeePreview(invoice.id, previewEnabled);
  const [target, setTarget] = useState<InvoiceLateFee | null>(null);
  const previewText = previewEnabled && preview.data ? describeLateFeePreview(preview.data) : null;
  // Quiet by design: nothing while loading, one small caption if it could not be worked out.
  const previewLine = previewEnabled && preview.isError ? (
    <Typography variant="caption" color="text.secondary" display="block">
      Could not work out the next late fee.
    </Typography>
  ) : previewText ? (
    <Typography variant="body2" color="text.secondary" sx={{ mb: fees.length > 0 ? 1 : 0 }}>
      {previewText}
    </Typography>
  ) : null;
  if (fees.length === 0 && !previewLine) return null;

  // A paid or void invoice's fee cannot be waived here (the server refuses it too).
  const waivable = canWaive && invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && invoice.status !== 'REFUNDED';

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Late fees
      </Typography>
      {previewLine}
      <Stack spacing={1}>
        {fees.map((fee) => (
          <FeeRow
            key={fee.id}
            fee={fee}
            timeZone={timeZone}
            onWaive={waivable && !fee.waivedAt ? () => { waiveMutation.reset(); setTarget(fee); } : undefined}
          />
        ))}
      </Stack>
      <LateFeeReasonDialog
        open={!!target}
        title={target ? `Waive late fee ${target.sequence}` : 'Waive late fee'}
        explanation={[
          `The ${target ? formatCurrencyFromDollars(parseFloat(target.amount)) : ''} fee stays on the invoice, struck through, and no longer counts towards what the family owes.`,
          'The family is emailed that it was waived (unless the Late Fee Waived email is switched off in System Emails). They never see your reason.',
          'A waived fee is never charged again; the next fee, if any, comes on its own date.',
        ]}
        label="Why is it being waived?"
        confirmLabel="Waive fee"
        loading={waiveMutation.isPending}
        error={waiveMutation.error}
        onClose={() => setTarget(null)}
        onConfirm={(reason) => {
          if (!target) return;
          waiveMutation.mutate({ id: invoice.id, feeId: target.id, reason }, { onSuccess: () => setTarget(null) });
        }}
      />
    </Box>
  );
}
